import {
  Button,
  Group,
  LoadingOverlay,
  Modal,
  NumberInput,
  Select,
  TextInput,
} from '@mantine/core'
import { useForm } from '@mantine/form'
import { notifications } from '@mantine/notifications'
import React from 'react'
import styles from '../../styles/admin/ChapterList.module.scss'

import { useChaptersInsert } from '../../api/hooks/Chapters/useChaptersInsert'
import { ChaptersInsertData } from '../../types/chapters'
import SaveButton from '../common/SaveButton'
import BaseButton from '../common/BaseButton'
import { Icon } from '../../assets/icons'

interface ChapterAddModalProps {
  opened: boolean
  onClose: () => void
}

interface FormValues {
  LEVEL: string
  MAIN?: string
  SUB?: string
  UNIT?: string
  DETAIL_UNIT?: string
  DEPTH2?: number
  DEPTH3?: number
  DEPTH4?: number
  DEPTH5?: number
}

const ChapterAddModal: React.FC<ChapterAddModalProps> = ({
  opened,
  onClose,
}) => {
  const { mutateAsync: insertChapter, isPending } = useChaptersInsert()

  const levelOptions = [
    { value: '1', label: 'level 1' },
    { value: '2', label: 'level 2' },
    { value: '3', label: 'level 3' },
    { value: '4', label: 'level 4' },
    { value: '5', label: 'level 5' },
    { value: '6', label: 'level 6' },
    { value: '7', label: 'level 7' },
    { value: '8', label: 'level 8' },
    { value: '9', label: 'level 9' },
    { value: '10', label: 'level 10' },
    { value: '11', label: 'level 11' },
    { value: '12', label: 'level 12' },
    { value: '99', label: 'level 99' },
  ]

  const form = useForm<FormValues>({
    initialValues: {
      LEVEL: '1',
      DEPTH2: 1,
      DEPTH3: 1,
      DEPTH4: 1,
      DEPTH5: 1,
      MAIN: '',
      SUB: '',
      UNIT: '',
      DETAIL_UNIT: '',
    },
    validate: {
      LEVEL: (value) =>
        !value || value.trim().length === 0 ? 'Level is required' : null,
      DEPTH2: (value) =>
        value !== undefined && value < 1 ? 'Depth 2 must be at least 1' : null,
      DEPTH3: (value) =>
        value !== undefined && value < 1 ? 'Depth 3 must be at least 1' : null,
      DEPTH4: (value) =>
        value !== undefined && value < 1 ? 'Depth 4 must be at least 1' : null,
      DEPTH5: (value) =>
        value !== undefined && value < 1 ? 'Depth 5 must be at least 1' : null,
    },
  })

  const handleSubmit = async (values: FormValues) => {
    try {
      const submitData: ChaptersInsertData = {
        LEVEL: values.LEVEL,
        DEPTH2: values.DEPTH2,
        DEPTH3: values.DEPTH3,
        DEPTH4: values.DEPTH4,
        DEPTH5: values.DEPTH5,
        MAIN: values.MAIN,
        SUB: values.SUB,
        UNIT: values.UNIT,
        DETAIL_UNIT: values.DETAIL_UNIT,
      }
      await insertChapter(submitData)

      notifications.show({
        title: 'Success',
        message: 'Chapter added successfully',
        color: 'green',
      })

      form.reset()
      onClose()
    } catch (error) {
      notifications.show({
        title: 'Error',
        message:
          error instanceof Error ? error.message : 'Failed to add chapter',
        color: 'red',
      })
    }
  }

  const handleClose = () => {
    if (!isPending) {
      form.reset()
      onClose()
    }
  }

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title="Add New Chapter"
      size="lg"
      closeOnClickOutside={!isPending}
      closeOnEscape={!isPending}
      styles={{
        content: {
          borderRadius: '15px',
        },
        close: {
          top: '-30px',
          right: '20px',
        },
      }}
    >
      <div style={{ position: 'relative', padding: '20px' }}>
        <LoadingOverlay visible={isPending} />

        <form onSubmit={form.onSubmit(handleSubmit)}>
          <div style={{ marginBottom: '1rem' }}>
            <Select
              label="Level *"
              placeholder="Select level"
              data={levelOptions}
              disabled={isPending}
              {...form.getInputProps('LEVEL')}
              searchable
              clearable={false}
              styles={{
                label: {
                  marginBottom: '8px',
                },
              }}
            />
          </div>

          <div
            style={{
              display: 'flex',
              gap: '1rem',
              alignItems: 'flex-start',
              width: '100%',
            }}
          >
            <div style={{ width: '20%', minWidth: 0 }}>
              <div style={{ marginBottom: '1rem' }}>
                <NumberInput
                  label="Depth 2 *"
                  placeholder="Enter depth 2"
                  min={1}
                  disabled={isPending}
                  {...form.getInputProps('DEPTH2')}
                  styles={{
                    label: {
                      marginBottom: '8px',
                    },
                  }}
                />
              </div>
              <div style={{ marginBottom: '1rem' }}>
                <NumberInput
                  label="Depth 3 *"
                  placeholder="Enter depth 3"
                  min={1}
                  disabled={isPending}
                  {...form.getInputProps('DEPTH3')}
                  styles={{
                    label: {
                      marginBottom: '8px',
                    },
                  }}
                />
              </div>
              <div style={{ marginBottom: '1rem' }}>
                <NumberInput
                  label="Depth 4 *"
                  placeholder="Enter depth 4"
                  min={1}
                  disabled={isPending}
                  {...form.getInputProps('DEPTH4')}
                  styles={{
                    label: {
                      marginBottom: '8px',
                    },
                  }}
                />
              </div>
              <div>
                <NumberInput
                  label="Depth 5 *"
                  placeholder="Enter depth 5"
                  min={1}
                  disabled={isPending}
                  {...form.getInputProps('DEPTH5')}
                  styles={{
                    label: {
                      marginBottom: '8px',
                    },
                  }}
                />
              </div>
            </div>

            <div style={{ width: '80%', minWidth: 0 }}>
              <div style={{ marginBottom: '1rem' }}>
                <TextInput
                  label="Main Chapter"
                  placeholder="Enter main chapter"
                  disabled={isPending}
                  {...form.getInputProps('MAIN')}
                  styles={{
                    label: {
                      marginBottom: '8px',
                    },
                  }}
                />
              </div>
              <div style={{ marginBottom: '1rem' }}>
                <TextInput
                  label="Sub Chapter"
                  placeholder="Enter sub chapter"
                  disabled={isPending}
                  {...form.getInputProps('SUB')}
                  styles={{
                    label: {
                      marginBottom: '8px',
                    },
                  }}
                />
              </div>
              <div style={{ marginBottom: '1rem' }}>
                <TextInput
                  label="Unit"
                  placeholder="Enter unit"
                  disabled={isPending}
                  {...form.getInputProps('UNIT')}
                  styles={{
                    label: {
                      marginBottom: '8px',
                    },
                  }}
                />
              </div>
              <div style={{ marginBottom: '1rem' }}>
                <TextInput
                  label="Detail Unit"
                  placeholder="Enter detail unit"
                  disabled={isPending}
                  {...form.getInputProps('DETAIL_UNIT')}
                  styles={{
                    label: {
                      marginBottom: '8px',
                    },
                  }}
                />
              </div>
            </div>
          </div>

          <Group justify="flex-end" mt="xl">
            <BaseButton type="submit" className={styles.addChapterButton}>
              Add Chapter
            </BaseButton>
            <BaseButton
              onClick={handleClose}
              disabled={isPending}
              className={styles.cancelButton}
            >
              Cancel
            </BaseButton>
          </Group>
        </form>
      </div>
    </Modal>
  )
}

export default ChapterAddModal
